import type { ReactNode } from "react";

import { Toast as BaseUIToast } from "@base-ui/react/toast";
import { RefreshIcon, TickIcon, WarningIcon } from "@eisox/icons";

import { Circle } from "../../Circle";
import { toastManager } from "../toast.store";
import type { ToastType } from "../toast.type";
import { Action, Close, Content, Description, Root, Title } from "./toast.component";

const toastIcons: Record<ToastType, { children: ReactNode; color: "green" | "red" | "darkBlue" | "blue" }> = {
  success: { children: <TickIcon />, color: "green" },
  error: { children: <WarningIcon />, color: "red" },
  warning: { children: <WarningIcon />, color: "darkBlue" },
  info: { children: "i", color: "blue" },
  loading: { children: <RefreshIcon className="animate-spin" />, color: "darkBlue" },
};

const ToastIcon = ({ type }: { type?: string }) => {
  if (type == null || !(type in toastIcons)) return null;

  const { children, color } = toastIcons[type as ToastType];
  return (
    <Circle.Root size={24} color={color} backgroundColor="white" className="h-6 w-6 shrink-0">
      {children}
    </Circle.Root>
  );
};

const ToastList = () => {
  const { toasts } = BaseUIToast.useToastManager();

  return toasts.map(toastItem => (
    <Root key={toastItem.id} toast={toastItem}>
      <Content>
        <ToastIcon type={toastItem.type} />
        <div className="gap-1 min-w-0 flex flex-1 flex-col">
          <Title />
          <Description />
        </div>
        <Action />
        <Close />
      </Content>
    </Root>
  ));
};

const Toaster = () => (
  <BaseUIToast.Provider toastManager={toastManager}>
    <BaseUIToast.Portal>
      <BaseUIToast.Viewport className="right-4 bottom-4 sm:w-90 pointer-events-auto fixed z-9999 w-[calc(100vw-2rem)]">
        <ToastList />
      </BaseUIToast.Viewport>
    </BaseUIToast.Portal>
  </BaseUIToast.Provider>
);

export { Toaster };
